import { PERSON_TYPE } from 'enums/enums'
import { mountStoreDevtool } from 'simple-zustand-devtools'
import { create } from 'zustand'
import { combine, persist, createJSONStorage } from 'zustand/middleware'

const TIPO_CONTRATACAO = ['DUPLO_SIM', 'FEP_WEB', 'NAO_ESCOLHIDO'] as const

type TipoContratacao = (typeof TIPO_CONTRATACAO)[number]

interface SimulationInfo {
  hasDocumentoObrigatorio: boolean
  person_type: PERSON_TYPE
  cd_inscricao_nacional_app: string
  billing: number
  isCorrentista: boolean
  tipoContratacao: TipoContratacao
  valorParcelaExistente: number
  valorParcelaAdmitido: number
  indicadorValorSuperiorPermitido: string
  descricaoValorSuperiorPermitido: string
  indicadorRestricaoCadastro: string
  descricaoRestricaoCadastro: string
  indicadorAprovacaoAnalise: string
  isDocumentSimulation: boolean
  idade_venda_maior: number
  idade_venda_menor: number
  dataAccountUserData: {
    id_conta_bancaria_dp: number
    id_conta_bancaria_pp: number
    id_forma_envio_demonstrativo: 'B'
    id_recebimento_dp: string
    id_recebimento_pp: string
    sn_uso_cheque_especial: 'S' | 'N' | null
    sn_cobranca_obrigacoes_vencidas: 'S' | 'N' | null
  }
  contaCorrentista: {
    no_agencia: string
    no_conta_corrente: string
    dg_conta_corrente: string
    dg_agencia: string
  }
}

const DEFAULT_SIMULATION_INFO: SimulationInfo = {
  hasDocumentoObrigatorio: false,
  person_type: '' as PERSON_TYPE,
  cd_inscricao_nacional_app: '',
  billing: 0,
  isCorrentista: false,
  dataAccountUserData: {
    id_conta_bancaria_dp: 0,
    id_conta_bancaria_pp: 0,
    id_forma_envio_demonstrativo: 'B',
    id_recebimento_dp: '',
    id_recebimento_pp: '',
    sn_uso_cheque_especial: null,
    sn_cobranca_obrigacoes_vencidas: null
  },
  contaCorrentista: {
    no_agencia: '',
    no_conta_corrente: '',
    dg_conta_corrente: '',
    dg_agencia: ''
  },
  tipoContratacao: 'NAO_ESCOLHIDO',
  valorParcelaExistente: 0,
  valorParcelaAdmitido: 0,
  indicadorValorSuperiorPermitido: '',
  descricaoValorSuperiorPermitido: '',
  indicadorRestricaoCadastro: '',
  descricaoRestricaoCadastro: '',
  indicadorAprovacaoAnalise: '',
  isDocumentSimulation: false,
  idade_venda_maior: 0,
  idade_venda_menor: 0
}

const useSimulationStore = create(
  persist(
    combine(DEFAULT_SIMULATION_INFO, (set) => ({
      setDocumentoObrigatorio: (hasDocumentoObrigatorio: boolean) =>
        set({ hasDocumentoObrigatorio }),
      setSimulation: (simulationInfo: SimulationInfo) => set(simulationInfo),
      setDataAccountUserData: (dataAccountUserData: SimulationInfo['dataAccountUserData']) =>
        set({ dataAccountUserData }),
      setSnChequeEspecial: (
        snChequeEspecial: SimulationInfo['dataAccountUserData']['sn_uso_cheque_especial']
      ) =>
        set((state) => ({
          dataAccountUserData: {
            ...state.dataAccountUserData,
            sn_uso_cheque_especial: snChequeEspecial
          }
        })),
      setSnCobrancasVencidas: (
        snCobrancasVencidas: SimulationInfo['dataAccountUserData']['sn_cobranca_obrigacoes_vencidas']
      ) =>
        set((state) => ({
          dataAccountUserData: {
            ...state.dataAccountUserData,
            sn_cobranca_obrigacoes_vencidas: snCobrancasVencidas
          }
        })),
      setContaCorrentista: (contaCorrentista: SimulationInfo['contaCorrentista']) =>
        set({ contaCorrentista }),
      resetSimulation: () => {
        localStorage.removeItem('simulation-info')
        set(DEFAULT_SIMULATION_INFO)
      }
    })),
    {
      name: 'simulation-info',
      storage: createJSONStorage(() => localStorage)
    }
  )
)

if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('Simulation Store', useSimulationStore)
}

export default useSimulationStore
